import { useMatch } from 'react-router';

import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { DropdownListMenu } from '#/components/Dropdown/menus/ListMenu/DropdownListMenu';
import {
  DropdownListMenuSection,
  DropdownListMenuSectionTitle,
} from '#/components/Dropdown/menus/ListMenu/DropdownListMenuSection';
import { Icon } from '#/components/Icon/Icon';
import { ProMemberBadge } from '#/components/ProMemberBadge/ProMemberBadge';
import { useI18n } from '#/i18n/hooks/useI18n';
import { LayoutHeaderButton } from '#/layouts/Layout/Header/Button/LayoutHeaderButton';
import {
  orgDashboardManageListingsPath,
  orgDashboardRootPath,
} from '#/modules/orgDashboard/routing/orgDashboard.paths';
import { userDashboardAccountSettingsPath } from '#/modules/userDashboard/routing/userDashboard.paths';
import { useRouteParams } from '#/routing/hooks/useRouteParams';
import { createOrgPath } from '#/routing/routes';

import {
  WorkspaceDropdownButtonContent,
  WorkspaceDropdownListMenuItem,
  WorkspaceDropdownProMemberBadgeContainer,
} from './WorkspaceDropdown.styled';

type Props = {
  user: ApiUser;
  wide?: boolean;
};

export function WorkspaceDropdown({ user, wide }: Props) {
  const { i18n } = useI18n();

  const { orgId: selectedOrgId } = useRouteParams(orgDashboardRootPath);
  const selectedOrg = selectedOrgId
    ? user?.orgs?.find((o) => o.id === selectedOrgId)
    : undefined;
  const personalDashboardMatch = useMatch(
    userDashboardAccountSettingsPath.with({}),
  );

  const isPersonalDashboard = Boolean(personalDashboardMatch);

  let dropdownText = i18n('Workspaces');

  if (isPersonalDashboard) {
    dropdownText = i18n('Personal Dashboard');
  } else if (selectedOrg) {
    dropdownText = selectedOrg.name;
  }

  return (
    <Dropdown
      trigger={
        <LayoutHeaderButton
          data={{ type: 'inert' }}
          variant="secondaryGrey"
          qaId="workspace-dropdown-toggle"
          ariaLabel={i18n('Workspace Selection')}
        >
          <WorkspaceDropdownButtonContent data-wide={wide || undefined}>
            <Icon name="building" size={16} />
            <div className="workspace-text">{dropdownText}</div>
            {wide && selectedOrg?.hasActiveOrgMembership && (
              <WorkspaceDropdownProMemberBadgeContainer>
                <ProMemberBadge />
              </WorkspaceDropdownProMemberBadgeContainer>
            )}
            <Icon name="chevron-updown" size={16} />
          </WorkspaceDropdownButtonContent>
        </LayoutHeaderButton>
      }
      trackClickedOpen={{ name: 'Workspace Dropdown' }}
    >
      <DropdownListMenu
        width={wide ? 400 : 240}
        maxHeight="calc(100vh - var(--root-padding-top) - 20px)"
        qaId="workspace-dropdown"
      >
        {user.orgs.length > 0 && (
          <DropdownListMenuSection>
            <DropdownListMenuSectionTitle>
              {i18n('Workspaces')}
            </DropdownListMenuSectionTitle>

            {user.orgs.map((org) => (
              <WorkspaceDropdownListMenuItem
                key={org.id}
                type="link"
                href={orgDashboardManageListingsPath.with({ orgId: org.id })}
                clickActions={{
                  closeRichDetailsFromWithin: true,
                  track: {
                    name: 'Updated Workspace - Org',
                  },
                }}
                qaId={
                  org.id === selectedOrgId
                    ? 'workspace-selected'
                    : 'workspace-unselected'
                }
                $selected={org.id === selectedOrgId}
              >
                <Icon
                  name={org.id === selectedOrgId ? 'simple-check' : 'building'}
                  size={16}
                />
                <span className="truncate">{org.name}</span>
              </WorkspaceDropdownListMenuItem>
            ))}

            <WorkspaceDropdownListMenuItem
              key="create"
              type="link"
              href={createOrgPath.with({})}
              clickActions={{
                closeRichDetailsFromWithin: true,
                track: {
                  name: 'Create a new organization',
                },
              }}
              qaId="workspace-unselected"
              $selected={false}
            >
              <Icon name="plus" size={16} />
              <span className="truncate">
                {i18n('Create a new organization')}
              </span>
            </WorkspaceDropdownListMenuItem>

            <WorkspaceDropdownListMenuItem
              key="personal"
              type="link"
              href={userDashboardAccountSettingsPath.with({})}
              clickActions={{
                closeRichDetailsFromWithin: true,
                track: {
                  name: 'Updated Workspace - Personal',
                },
              }}
              qaId={
                isPersonalDashboard
                  ? 'workspace-selected'
                  : 'workspace-unselected'
              }
              $selected={isPersonalDashboard}
            >
              <Icon
                name={isPersonalDashboard ? 'simple-check' : 'person'}
                size={16}
              />
              <span className="truncate">{i18n('Personal Dashboard')}</span>
            </WorkspaceDropdownListMenuItem>
          </DropdownListMenuSection>
        )}
      </DropdownListMenu>
    </Dropdown>
  );
}
